import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Send,
  Loader2,
  StopCircle,
  UserCircle,
  Bot,
  Clock,
  Shield,
  Zap,
  Flame,
  Snowflake,
  Smile,
  Mic,
  MicOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERSONA_ICONS = {
  budget_conscious: Snowflake,
  busy_decision_maker: Zap,
  technical_buyer: Shield,
  skeptical_buyer: Flame,
  friendly_noncommittal: Smile
};

const DIFFICULTY_COLORS = {
  easy: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  medium: 'bg-yellow-500/15 text-amber-600 border-yellow-500/30',
  hard: 'bg-red-500/15 text-red-600 border-red-500/30'
};

export default function RoleplaySession() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchSession();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (session?.status === 'active') {
      const startTime = new Date(session.created_at).getTime();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [session]);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
        setMessages(data.transcript || []);
        if (data.status === 'completed') {
          navigate(`/practice/results/${sessionId}`, { replace: true });
        }
      } else {
        toast.error('Session not found');
        navigate('/practice');
      }
    } catch (e) {
      toast.error('Failed to load session');
      navigate('/practice');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setInput('');
    setSending(true);

    const repMsg = { speaker: 'rep', timestamp: new Date().toISOString(), message: text };
    setMessages(prev => [...prev, repMsg]);

    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}/message`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text })
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data.message]);
      } else {
        toast.error('Failed to get response');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const endSession = async () => {
    if (ending) return;
    setEnding(true);

    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}/end`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        navigate(`/practice/results/${sessionId}`);
      } else {
        toast.error('Failed to end session');
        setEnding(false);
      }
    } catch (e) {
      toast.error('Failed to end session');
      setEnding(false);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!session) return null;

  const persona = session.persona || {};
  const BuyerIcon = PERSONA_ICONS[session.persona_id] || UserCircle;

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-80px)]" data-testid="roleplay-session">
        {/* Header Bar */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-card/50 backdrop-blur-sm rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
              <BuyerIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm text-foreground">{persona.name || 'AI Buyer'}</p>
              <p className="text-xs text-muted-foreground">{persona.job_title} — {persona.company_type}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={`${DIFFICULTY_COLORS[session.difficulty] || ''} border text-xs`}>
              {session.difficulty}
            </Badge>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground font-mono">
              <Clock className="w-3.5 h-3.5" />
              {formatTime(elapsed)}
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={endSession}
              disabled={ending}
              data-testid="end-session-btn"
              className="h-8"
            >
              {ending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <StopCircle className="w-4 h-4 mr-1" />}
              {ending ? 'Analyzing...' : 'End Session'}
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-area">
          {messages.map((msg, idx) => (
            <div key={`k1-${idx}`} className={`flex gap-3 ${msg.speaker === 'rep' ? 'justify-end' : 'justify-start'}`}>
              {msg.speaker === 'buyer' && (
                <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0 mt-1">
                  <BuyerIcon className="w-4 h-4 text-muted-foreground" />
                </div>
              )}
              <div className={`max-w-[70%] ${
                msg.speaker === 'rep'
                  ? 'bg-primary/15 border border-primary/30 rounded-2xl rounded-br-sm'
                  : 'bg-secondary/60 border border-border/50 rounded-2xl rounded-bl-sm'
              } px-4 py-3`}>
                <p className="text-sm text-foreground leading-relaxed">{msg.message}</p>
                <p className="text-[10px] text-muted-foreground/50 mt-1">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {msg.speaker === 'rep' && (
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0 mt-1">
                  <UserCircle className="w-4 h-4 text-primary" />
                </div>
              )}
            </div>
          ))}

          {sending && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                <BuyerIcon className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="bg-secondary/60 border border-border/50 rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border bg-card/50 backdrop-blur-sm">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Type your response..."
              disabled={sending || ending}
              autoFocus
              data-testid="message-input"
              className="flex-1 h-11 px-4 rounded-xl bg-background border border-border text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 disabled:opacity-50"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || sending || ending}
              data-testid="send-btn"
              className="h-11 w-11 rounded-xl p-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground/40 mt-2 text-center">
            Stay in your role as a sales rep. The AI buyer will challenge you realistically.
          </p>
        </div>
      </div>
    </Layout>
  );
}
